import React from 'react';
import { Create, required, List, SaveButton, Datagrid, DateField, TextField, BooleanField, Edit, FormTab, TabbedForm, Toolbar, BooleanInput, TextInput, Filter, SelectInput, useInput, FormDataConsumer, FunctionField } from 'react-admin';
import { ColorInput } from './components/utils';

const disableableResources = {
    'Inventory': [
        { id: 'publishers', name: 'Publishers' },
        { id: 'sites', name: 'Sites' },
        { id: 'placements', name: 'Placements' },
        { id: 'batch', name: 'Batch Sites' },
        { id: 'batchp', name: 'Batch Placements' },
        { id: 'batchd', name: 'Batch Domains' },
        { id: 'adunits', name: 'Ad Units' },
        { id: 'gam_accounts', name: 'GAM Accounts' },
        { id: 'domains', name: 'Domains' },
    ],
    'Delivery': [
        { id: 'bidders', name: 'Bidders' },
        { id: 'campaigns', name: 'Traffic Rules' },
        { id: 'adcampaigns', name: 'Campaigns' },
        { id: 'creatives', name: 'Creatives' },
        { id: 'deals', name: 'Deals' },
        { id: 'advertisers', name: 'Advertisers' },
        { id: 'experiments', name: 'Experiments' },
        { id: 'ad_quality_rules', name: 'Ad Quality Rules' },
    ],
    'Reports': [
        { id: 'pubreports', name: 'Earning Reports' },
        { id: 'realtime', name: 'Realtime Dash' },
        { id: 'topsites', name: 'Market Movers' },
        { id: 'predictions', name: 'Bid Predictions' },
        { id: 'ivt', name: 'IVT' },
        { id: 'adcost', name: 'Ad Requests' },
        { id: 'clicks', name: 'Click Reports' },
        { id: 'auctions', name: 'Auction Reports' },
        { id: 'auction_optimizer', name: 'Optimizer' },
        { id: 'discrepancies', name: 'Discrepancy' },
        { id: 'adViewability', name: 'Viewability' },
        { id: 'ad_domains', name: 'Ad Domains' },
        { id: 'missing_sites', name: 'Missing Sites' },
        { id: 'ad_malware', name: 'Ad Malware' },
        { id: 'adstxtreports', name: 'Adstxt Coverage' },
        { id: 'adquality_reports', name: 'Ad Quality Reports' },
        { id: 'traffic_reports', name: 'Traffic Reports' },
        { id: 'advertiser_cpm', name: 'CPM Comparison' },
        { id: 'do_cost_report', name: 'Cost Explorer' },
        { id: 'exp_dash', name: 'Experiments' },
    ],
    'Analytics Adapter': [
        { id: 'carbon', name: 'Analytics Adapter' },
        { id: 'carbon/dashboard', name: 'Dashboard' },
        { id: 'carbon/pubreports', name: 'Reports' },
        { id: 'carbon/discrepancies', name: 'Discrepancies' },
        { id: 'carbon/realtime', name: 'Realtime' },
        { id: 'carbon/carbonizer', name: 'Optimizer' },
        { id: 'carbon/settings', name: 'Settings' },
    ],
    'Admin': [
        { id: 'users', name: 'Users' },
        { id: 'logs', name: 'Logbook' },
        { id: 'alarms', name: 'Alarms' },
        { id: 'share', name: 'Share' },
        { id: 'secrets', name: 'Secrets' },
        { id: 'settings', name: 'Settings' },
        { id: 'tools', name: 'Tools' },
    ],
};

const disableableFeatures = {
    'Site Features': [
        { id: 'custom_ad_formats', name: 'Custom Ad Formats' },
        { id: 'predictions', name: 'Predictions' },
        { id: 'prebid_wrapper', name: 'Prebid Wrapper' },
    ],
    'Settings Tabs': [
        { id: 'settings_tab_general', name: 'Settings (General)' },
        { id: 'settings_tab_trackers', name: 'Trackers' },
        { id: 'settings_tab_markups', name: 'Markups' },
        { id: 'settings_tab_native_templates', name: 'Native Templates' },
        { id: 'settings_tab_combo', name: 'Combo' },
        { id: 'settings_tab_a2v', name: 'A2V' },
        { id: 'settings_tab_auction_optimizer', name: 'Auction Optimizer' },
    ],
};

const GroupedCheckboxInput = ({ source, choices }) => {
    const groups = choices || disableableResources;
    const { input } = useInput({ source });
    const selected = input.value || [];

    const toggle = (id) => {
        const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
        input.onChange(next);
    };

    const toggleGroup = (items, checked) => {
        const ids = items.map(i => i.id);
        const next = checked ? [...new Set([...selected, ...ids])] : selected.filter(x => !ids.includes(x));
        input.onChange(next);
    };

    return (
        <div>
            <p style={{ color: '#999', fontSize: 14, marginBottom: 16 }}>Checked items will be disabled for this organization's users</p>
            {Object.entries(groups).map(([group, items]) => {
                const allChecked = items.every(i => selected.includes(i.id));
                const someChecked = items.some(i => selected.includes(i.id));
                return (
                    <div key={group} style={{ marginBottom: 16 }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'bold', fontSize: 16 }}>
                            <input
                                type="checkbox"
                                checked={allChecked}
                                ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                                onChange={() => toggleGroup(items, !allChecked)}
                                style={{ marginRight: 8 }}
                            />
                            {group}
                        </label>
                        <div style={{ marginLeft: 28, display: 'grid', gridTemplateColumns: 'repeat(3, 200px)', gap: '4px 24px' }}>
                            {items.map(item => (
                                <label key={item.id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 14, minWidth: 160 }}>
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(item.id)}
                                        onChange={() => toggle(item.id)}
                                        style={{ marginRight: 6 }}
                                    />
                                    {item.name}
                                </label>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const OrganizationTitle = ({ record }) => {
    return <span>Organization {record ? `"${record.name}"` : ''}</span>;
};

const OrganizationFilter = (props) => (
    <Filter {...props}>
        <TextInput label="Search" source="name" alwaysOn resettable />
    </Filter>
);

export const OrganizationList = (props) => (
    <List {...props} filters={<OrganizationFilter />} perPage={25} bulkActionButtons={false} exporter={false}>
        <Datagrid rowClick="edit">
            <TextField source="code" label="ID" />
            <FunctionField label="" render={record => record.branding && record.branding.favicon_url ? <img src={record.branding.favicon_url} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} /> : null} sortable={false} />
            <TextField source="name" label="Name" />
            <TextField source="slug" label="Slug" />
            <BooleanField source="active" label="Active" />
            <DateField source="updated_at" label="Updated" showTime />
        </Datagrid>
    </List>
);

const CustomToolbar = props => (
    <Toolbar {...props}>
        <SaveButton />
        <br />
        <DateField source="updated_at" label="Update date" style={{ padding: '10px' }} showTime />
        <TextField source="updated_name" label="" style={{ padding: '10px' }} />
    </Toolbar>
);

export const OrganizationEdit = (props) => {
    return (<Edit title={<OrganizationTitle />} {...props}>
        <TabbedForm toolbar={<CustomToolbar />}>
            <FormTab label="Organization">
                <TextField source="code" label="Organization ID" />
                <TextInput source="name" label="Name" validate={required()} style={{ width: '400px' }} />
                <TextInput source="slug" label="Slug" helperText="URL-safe identifier (e.g. 'acme'). Used for subdomain routing." validate={required()} style={{ width: '400px' }} />
                <BooleanInput source="active" label="Active" />
            </FormTab>
            <FormTab label="Branding">
                <TextInput source="branding.display_name" label="Display Name" style={{ width: '400px' }} />
                <TextInput source="branding.logo_url" label="Logo URL" helperText="URL to logo image (shown on login page and app bar)" style={{ width: '600px' }} />
                <FormDataConsumer>
                    {({ formData }) => formData && formData.branding && formData.branding.logo_url ? (
                        <div style={{ marginBottom: 16 }}>
                            <img src={formData.branding.logo_url} alt="Logo preview" style={{ maxHeight: 60, maxWidth: 300, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} onLoad={(e) => { e.target.style.display = 'inline'; }} />
                        </div>
                    ) : null}
                </FormDataConsumer>
                <TextInput source="branding.favicon_url" label="Favicon URL" style={{ width: '600px' }} />
                <FormDataConsumer>
                    {({ formData }) => formData && formData.branding && formData.branding.favicon_url ? (
                        <div style={{ marginBottom: 16 }}>
                            <img src={formData.branding.favicon_url} alt="Favicon preview" style={{ maxHeight: 32, maxWidth: 32, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} onLoad={(e) => { e.target.style.display = 'inline'; }} />
                        </div>
                    ) : null}
                </FormDataConsumer>
                <TextInput source="branding.copyright_text" label="Copyright Text" style={{ width: '400px' }} />
                <TextInput source="branding.copyright_url" label="Copyright URL" style={{ width: '400px' }} />
                <ColorInput source="branding.primary_color" label="Primary Color" helperText="Hex color (e.g. #E91E63)" />
                <ColorInput source="branding.primary_light" label="Primary Light" />
                <ColorInput source="branding.primary_dark" label="Primary Dark" />
                <ColorInput source="branding.secondary_color" label="Secondary Color" />
                <SelectInput source="branding.palette_type" label="Palette Type" choices={[
                    { id: 'dark', name: 'Dark' },
                    { id: 'light', name: 'Light' },
                ]} />
            </FormTab>
            <FormTab label="Supply Chain">
                <TextInput source="supply_chain.asi" label="ASI" helperText="Advertising System Identifier (e.g. 'nobid.io')" validate={required()} style={{ width: '400px' }} />
                <TextInput source="supply_chain.seller_id" label="Seller ID" style={{ width: '400px' }} />
                <TextInput source="supply_chain.seller_name" label="Seller Name" style={{ width: '400px' }} />
                <TextInput source="supply_chain.domain" label="Domain" style={{ width: '400px' }} />
                <SelectInput source="supply_chain.handles_payment" label="Handles Payment" choices={[
                    { id: 1, name: 'Yes' },
                    { id: 0, name: 'No' },
                ]} />
                <TextInput source="supply_chain.address" label="Address" helperText="Company address for sellers.json" style={{ width: '600px' }} />
                <TextInput source="supply_chain.contact_email" label="Contact Email" helperText="Contact email for sellers.json" style={{ width: '400px' }} />
            </FormTab>
            <FormTab label="Menu">
                <GroupedCheckboxInput source="disabled_resources" />
            </FormTab>
            <FormTab label="Features">
                <GroupedCheckboxInput source="disabled_features" choices={disableableFeatures} />
            </FormTab>
        </TabbedForm>
    </Edit>)
};

export const OrganizationCreate = (props) => (
    <Create title="Create Organization" {...props}>
        <TabbedForm>
            <FormTab label="Organization">
                <TextInput source="name" label="Name" validate={required()} style={{ width: '400px' }} />
                <TextInput source="slug" label="Slug" helperText="URL-safe identifier (e.g. 'acme'). Used for subdomain routing." validate={required()} style={{ width: '400px' }} />
                <BooleanInput source="active" label="Active" defaultValue={true} />
            </FormTab>
            <FormTab label="Branding">
                <TextInput source="branding.display_name" label="Display Name" style={{ width: '400px' }} />
                <TextInput source="branding.logo_url" label="Logo URL" style={{ width: '600px' }} />
                <TextInput source="branding.copyright_text" label="Copyright Text" style={{ width: '400px' }} />
                <TextInput source="branding.copyright_url" label="Copyright URL" style={{ width: '400px' }} />
                <ColorInput source="branding.primary_color" label="Primary Color" helperText="Hex color (e.g. #E91E63)" />
                <ColorInput source="branding.primary_light" label="Primary Light" />
                <ColorInput source="branding.primary_dark" label="Primary Dark" />
                <ColorInput source="branding.secondary_color" label="Secondary Color" />
                <SelectInput source="branding.palette_type" label="Palette Type" choices={[
                    { id: 'dark', name: 'Dark' },
                    { id: 'light', name: 'Light' },
                ]} defaultValue="dark" />
            </FormTab>
            <FormTab label="Supply Chain">
                <TextInput source="supply_chain.asi" label="ASI" helperText="Advertising System Identifier (e.g. 'acme-ssp.com')" validate={required()} style={{ width: '400px' }} />
                <TextInput source="supply_chain.seller_name" label="Seller Name" style={{ width: '400px' }} />
                <TextInput source="supply_chain.domain" label="Domain" style={{ width: '400px' }} />
                <SelectInput source="supply_chain.handles_payment" label="Handles Payment" choices={[
                    { id: 1, name: 'Yes' },
                    { id: 0, name: 'No' },
                ]} defaultValue={1} />
                <TextInput source="supply_chain.address" label="Address" helperText="Company address for sellers.json" style={{ width: '600px' }} />
                <TextInput source="supply_chain.contact_email" label="Contact Email" helperText="Contact email for sellers.json" style={{ width: '400px' }} />
            </FormTab>
            <FormTab label="Menu">
                <GroupedCheckboxInput source="disabled_resources" />
            </FormTab>
            <FormTab label="Features">
                <GroupedCheckboxInput source="disabled_features" choices={disableableFeatures} />
            </FormTab>
        </TabbedForm>
    </Create>
);
